import React, { useState, useEffect, useRef, useCallback } from "react";

type Particle = {
	left: number;
	top: number;
	x: number;
	y: number;
	color: string;
};

interface ChangeTextEffectProps {
	initialText?: string;
	newText?: string;
	transitionDuration?: number;
	numParticles?: number;
	colors?: string[];
	shouldAnimate?: boolean;
}

const ChangeTextEffect: React.FC<ChangeTextEffectProps> = ({
	initialText = "0",
	newText = "1",
	transitionDuration = 500,
	numParticles = 50,
	colors = [
		"red",
		"blue",
		"yellow",
		"green",
		"purple",
		"orange",
		"pink",
		"cyan",
		"magenta",
		"lime",
	],
	shouldAnimate
}) => {
	const [text, setText] = useState(initialText);
	const [isTransitioning, setIsTransitioning] = useState(false);
	const [particles, setParticles] = useState<Particle[]>([]);
	const textRef = useRef<HTMLDivElement | null>(null);

	const createParticles = useCallback((centerX: number, centerY: number) => {
		const particles: Particle[] = [];
		for (let i = 0; i < numParticles; i++) {
			const x = (Math.random() - 0.5) * 200;
			const y = (Math.random() - 0.5) * 200;
			const color = colors[Math.floor(Math.random() * colors.length)];
			particles.push({ left: centerX, top: centerY, x, y, color });
		}
		return particles;
	}, [numParticles, colors]);

	useEffect(() => {
		if (!textRef.current || newText === text) return;

		if (shouldAnimate) {
			// 1) スライドアウトアニメーションを開始する
			setIsTransitioning(true);

			// スライドアウトが完了した後...
			const slideOutTimer = setTimeout(() => {
				// 2) 新しいテキストに更新し、スライドインを開始する
				setText(newText);
				setIsTransitioning(false);

				// 3) スライドインが完了したら、花火を表示する
				const slideInTimer = setTimeout(() => {
					// 中心位置を計算する
					const rect = textRef.current!.getBoundingClientRect();
					const containerRect = textRef.current!.offsetParent?.getBoundingClientRect();
					if (!containerRect) return;
					const centerX = rect.left + rect.width / 2 - containerRect.left;
					const centerY = rect.top + rect.height / 2 - containerRect.top;

					// パーティクルを作成して表示する
					const newParticles = createParticles(centerX, centerY);
					setParticles(newParticles);

					// 600ms後にパーティクルを消す
					const clearParticles = setTimeout(() => setParticles([]), 600);
					return () => clearTimeout(clearParticles);
				}, transitionDuration);

				return () => clearTimeout(slideInTimer);
			}, transitionDuration);

			return () => clearTimeout(slideOutTimer);
		} else {
			// shouldAnimateがfalseの場合、アニメーションなしでテキストを更新する
			setText(newText);
			setIsTransitioning(false);
			setParticles([]);
		}
	}, [newText, text, transitionDuration, createParticles, shouldAnimate]);


	return (
		<div style={{ position: "relative", textAlign: "center" }}>
			<div
				ref={textRef}
				className={`text-container slide-effect ${isTransitioning ? "transitioning" : ""}`}
				style={{
					fontSize: "2.5rem",
					fontWeight: "bold",
					transition: `all ${transitionDuration / 1000}s ease`,
				}}
			>
				{text}
			</div>
			<div
				style={{
					position: "absolute",
					top: 0,
					left: 0,
					width: "100%",
					height: "100%",
					pointerEvents: "none",
					zIndex: 0,
				}}
			>
				{particles.map((p, i) => (
					<div
						key={i}
						className="firework"
						style={{
							left: `${p.left}px`,
							top: `${p.top}px`,
							backgroundColor: p.color,
							position: "absolute",
							width: "5px",
							height: "5px",
							borderRadius: "50%",
							"--x": `${p.x}px`,
							"--y": `${p.y}px`,
						} as React.CSSProperties & { [key: string]: string }}
					/>
				))}
			</div>
		</div>
	);
};

export default ChangeTextEffect;
